import React, { useState, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/Blog")({
  component: BlogPage,
});

const POSTS = [
  {
    id: "LOG_01",
    ref: "SYS-ARCH",
    title: "Modernizing Legacy Systems with .NET Core",
    excerpt: "Architectural patterns for transforming monolithic WinForms into cloud-native solutions.",
    date: "2024.10.24",
    category: "ENGINEERING",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1000&auto=format&fit=crop",
    readTime: "6 MIN",
  },
  {
    id: "LOG_02",
    ref: "UI-UX",
    title: "React vs Svelte in 2025",
    excerpt: "A deep dive into compiler-led optimization and the shift away from Virtual DOM.",
    date: "2024.11.12",
    category: "FRONTEND",
    image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=1000&auto=format&fit=crop",
    readTime: "4 MIN",
  },
{
    id: "LOG_04",
    ref: "DEV-OPS",
    title: "The Zero-Config CI/CD Pipe",
    excerpt: "How I reduced deployment friction using GitHub Actions and automated staging environments.",
    date: "2024.12.20",
    category: "INFRASTRUCTURE",
    image: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?auto=format&fit=crop&q=80&w=1000",
    readTime: "5 MIN",
  },
  {
    id: "LOG_05",
    ref: "AI-INTEG",
    title: "Beyond the Chatbot: LLMs in ERP",
    excerpt: "Using OpenAI's function calling to parse unstructured PDF purchase orders into structured SQL data.",
    date: "2025.01.10",
    category: "AI_AUTOMATION",
    image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1000",
    readTime: "7 MIN",
  },
  {
    id: "LOG_06",
    ref: "SOFT-SKILLS",
    title: "Debugging the Developer Mindset",
    excerpt: "The psychological shift required to move from 'Senior Dev' to 'Product Architect'.",
    date: "2025.01.28",
    category: "PHILOSOPHY",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1000",
    readTime: "3 MIN",
  },
  {
    id: "LOG_07",
    ref: "SEC-HARD",
    title: "Securing the Perimeter: Zero Trust",
    excerpt: "Why traditional VPNs are failing and how to implement Identity-Aware Proxies for internal tools.",
    date: "2025.02.15",
    category: "SECURITY",
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&q=80&w=1000",
    readTime: "6 MIN",
  },
  {
    id: "LOG_08",
    ref: "PERF-OPT",
    title: "Fighting the Bloat: 100ms Goals",
    excerpt: "Optimization techniques for React apps that deal with massive real-time data tables.",
    date: "2025.03.02",
    category: "FRONTEND",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1000",
    readTime: "4 MIN",
  }
];

function BlogItem({ post }: { post: typeof POSTS[0] }) {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  return (
    <section
      style={{
        ...rowStyle,
        backgroundColor: isHovered ? "#161616" : "transparent",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => console.log("Navigate to post")}
    >
      <div style={metaColumn}>
        <span style={idStyle}>REF_{post.id}</span>
        <span style={codeStyle}>{post.ref}</span>
      </div>

      <div style={contentColumn}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          <span style={categoryStyle}>{post.category}</span>
          <span style={idStyle}>// {post.readTime}</span>
        </div>
        <h2 style={postTitleStyle}>{post.title}</h2>
        <p style={descStyle}>{post.excerpt}</p>
        <div style={footerRow}>
          <span style={dateStyle}>PUBLISHED: {post.date}</span>
          <span style={{ ...idStyle, color: isHovered ? "#fff" : "#444" }}>READ_ARTICLE _&gt;</span>
        </div>
      </div>

      <div style={imageColumn}>
        <div style={imageFrame}>
          <img 
            src={post.image} 
            alt={post.title} 
            style={{ ...imgStyle, opacity: isHovered ? 0.8 : 0.4 }} 
          />
        </div>
      </div>
    </section>
  );
}

export function BlogPage() {
  return (
    <div style={containerStyle}>
      {/* Navigation matching Portfolio/Home */}
      <nav style={navStyle}>
        <div style={navBrand}>
          <div style={avatarStyle}>JF</div>
          <span style={{ fontWeight: "bold", letterSpacing: "1px" }}>JOSH_FLORES</span>
        </div>
        <div style={navLinks}>
          <Link to="/Home" style={navLink}>HOME</Link>
          <Link to="/Portfolio" style={navLink}>PORTFOLIO</Link>
          <Link to="/Blog" style={{ ...navLink, color: "#fff" }}>BLOG</Link>
          <a href="mailto:joshflor07@gmail.com" style={navLink}>CONTACT</a>
        </div>
      </nav>

      <header style={headerStyle}>
        <div style={{ borderLeft: "2px solid #333", paddingLeft: "20px" }}>
          <h1 style={{ fontSize: "24px", margin: 0, color: "#fff" }}>BLOG</h1>
          <p style={{ fontSize: "12px", color: "#666", margin: "5px 0 0 0" }}>UNFILTERED TECHNICAL DOCUMENTATION & THOUGHTS</p>
        </div>
      </header>

      <main style={mainContent}>
        {POSTS.map((post) => (
          <BlogItem key={post.id} post={post} />
        ))}
      </main>
    </div>
  );
}

/* STYLING - Reused from Portfolio for consistency */

const containerStyle: React.CSSProperties = {
  backgroundColor: "#0a0a0a",
  color: "#e0e0e0",
  minHeight: "100vh",
  padding: "120px 24px 60px 24px",
  fontFamily: "monospace",
};

const navStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  height: "70px",
  backgroundColor: "rgba(10, 10, 10, 0.8)",
  backdropFilter: "blur(12px)",
  borderBottom: "1px solid #222",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0 40px",
  zIndex: 1000,
};

const navBrand: React.CSSProperties = { display: "flex", alignItems: "center", gap: "12px" };
const navLinks: React.CSSProperties = { display: "flex", gap: "30px" };
const navLink: React.CSSProperties = { textDecoration: "none", color: "#666", fontSize: "12px", fontWeight: "bold" };
const avatarStyle: React.CSSProperties = { width: "32px", height: "32px", backgroundColor: "#1a1a1a", border: "1px solid #333", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "12px", fontWeight: "bold", borderRadius: "8px" };

const headerStyle: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto 40px auto", padding: "0 40px" };
const mainContent: React.CSSProperties = { maxWidth: "1000px", margin: "0 auto" };

const rowStyle: React.CSSProperties = {
  display: "flex",
  padding: "40px",
  gap: "40px",
  borderRadius: "20px",
  transition: "all 0.2s ease",
  cursor: "pointer",
  marginBottom: "10px",
  border: "1px solid transparent",
};

const metaColumn: React.CSSProperties = { width: "80px", display: "flex", flexDirection: "column", gap: "5px" };
const idStyle: React.CSSProperties = { fontSize: "10px", color: "#444" };
const codeStyle: React.CSSProperties = { fontSize: "12px", color: "#666", fontWeight: "bold" };

const contentColumn: React.CSSProperties = { flex: 2 };
const categoryStyle: React.CSSProperties = { fontSize: "10px", color: "#3b82f6", fontWeight: "bold", letterSpacing: "1px" };
const postTitleStyle: React.CSSProperties = { fontSize: "20px", margin: "0 0 15px 0", color: "#fff", fontWeight: "bold" };
const descStyle: React.CSSProperties = { fontSize: "14px", color: "#888", lineHeight: "1.6", marginBottom: "20px" };

const footerRow: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const dateStyle: React.CSSProperties = { fontSize: "11px", color: "#444" };

const imageColumn: React.CSSProperties = { flex: 1, display: "flex", alignItems: "center" };
const imageFrame: React.CSSProperties = { borderRadius: "12px", overflow: "hidden", border: "1px solid #222", width: "100%" };
const imgStyle: React.CSSProperties = { width: "100%", height: "140px", objectFit: "cover", transition: "opacity 0.3s ease" };

export default BlogPage;